import classNames from 'classnames';
import React, {useCallback} from 'react';

import {
    BackendSourceOptions,
    DiscoveryItemClickEvent,
    DiscoveryItemImpressionEvent,
} from '@udemy/browse-event-tracking';
import {Tracker, TrackImpression, generateTrackingId} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {
    CardDescription,
    CardMediaImage,
    CourseProductCard as BaseCourseProductCard,
    RatingTag,
    Tag,
} from '@udemy/react-product-card-components';
import {CourseCardSkeleton} from '@udemy/react-reveal-components';

import {CardTitle, GoogleCourseCardAuthors} from '../card-helpers';
import {CarouselListLayout} from '../free-resources-section/carousel-list-layout';
import styles from './courses-carousel-list.module.less';

import type {Course} from 'src/generated/graphql';

const DEFAULT_UI_REGION = 'bundle-unit';

type CourseProductCardProps = {
    course: Course;
    index: number;
    uiRegion?: string;
};

export type CoursesCarouselListProps = {
    courses: Course[];
    isLoading: boolean;
    totalCount?: number;
    maxCoursesPerRow: number;
    uiRegion?: string;
    className?: string;
};

const CourseProductCardSkeleton = (): JSX.Element => (
    <div
        className={classNames(
            styles['resource-course-item'],
            styles['resource-course-skeleton-item'],
        )}
    >
        <CourseCardSkeleton />
    </div>
);

const CourseProductCard = ({course, index, uiRegion}: CourseProductCardProps): JSX.Element => {
    const {gettext, ninterpolate} = useI18n();
    const courseId = Number(course.id);
    const trackingId = String(course.id);

    const durationInSeconds = Number(course.durationInSeconds ?? 0);
    const durationMinutes = durationInSeconds > 0 ? durationInSeconds / 60 : 0;
    const durationTag =
        durationMinutes > 0
            ? durationMinutes < 60
                ? ninterpolate('%(minutes)s min', '%(minutes)s mins', Math.round(durationMinutes), {
                      minutes: Math.round(durationMinutes).toFixed(0),
                  })
                : ninterpolate(
                      '%(hours)s hour',
                      '%(hours)s hours',
                      Math.floor(durationMinutes / 60),
                      {
                          hours: Number((durationMinutes / 60).toFixed(1)),
                      },
                  )
            : null;

    const trackImpression = useCallback(
        () =>
            Tracker.publishEvent(
                new DiscoveryItemImpressionEvent({
                    id: courseId,
                    type: 'course',
                    trackingId: generateTrackingId(),
                    serveTrackingId: trackingId,
                    backendSource: BackendSourceOptions.DISCOVERY,
                    position: index + 1,
                    badgeFamilies: null,
                    relatedSourceId: null,
                    relatedSourceType: null,
                    uiRegion: uiRegion ?? DEFAULT_UI_REGION,
                }),
            ),
        [courseId, trackingId, index, uiRegion],
    );

    const trackClick = useCallback(
        () =>
            Tracker.publishEvent(
                new DiscoveryItemClickEvent({
                    id: courseId,
                    type: 'course',
                    trackingId: generateTrackingId(),
                    serveTrackingId: trackingId,
                    backendSource: BackendSourceOptions.DISCOVERY,
                    badgeFamilies: null,
                    uiRegion: uiRegion ?? DEFAULT_UI_REGION,
                }),
            ),
        [courseId, trackingId, uiRegion],
    );

    return (
        <TrackImpression trackFunc={trackImpression}>
            <div
                onClick={trackClick}
                role="presentation"
                className={styles['resource-course-item-wrapper']}
            >
                <BaseCourseProductCard
                    className={styles['resource-course-item']}
                    layout="vertical"
                    mediaSquare={
                        <CardMediaImage layout="square" src={course.images?.height200 ?? ''} />
                    }
                    mediaWide={
                        <CardMediaImage layout="wide" src={course.images?.px480x270 ?? ''} />
                    }
                    title={<CardTitle title={course.title} href={course.urlCourseLanding ?? ''} />}
                    description={<CardDescription>{course.headline ?? ''}</CardDescription>}
                    authors={<GoogleCourseCardAuthors />}
                    tags={[
                        <RatingTag key="tag-rating" rating={course.rating?.average ?? 0} />,
                        <Tag key="tag-course">{gettext('Course')}</Tag>,
                        ...(durationTag ? [<Tag key="tag-duration">{durationTag}</Tag>] : []),
                    ]}
                />
            </div>
        </TrackImpression>
    );
};

export const CoursesCarouselList = ({
    courses,
    isLoading,
    totalCount,
    maxCoursesPerRow,
    uiRegion,
    className,
}: CoursesCarouselListProps): JSX.Element => {
    const skeletonCount = Math.min(totalCount ?? maxCoursesPerRow, maxCoursesPerRow);
    const items = isLoading ? Array<Course>(skeletonCount).fill({} as Course) : courses;
    const totalItemsCount = isLoading ? skeletonCount : courses?.length ?? 0;
    const Component = isLoading ? CourseProductCardSkeleton : CourseProductCard;

    return (
        <div className={classNames(styles['courses-carousel-list'], className)}>
            <CarouselListLayout
                key="courses-carousel-list-layout"
                items={items}
                collapsedItemsCount={maxCoursesPerRow}
                totalItemsCount={totalItemsCount}
                renderItem={(item, index) => (
                    <Component key={index} course={item} index={index} uiRegion={uiRegion} />
                )}
            />
        </div>
    );
};
